'use client';
import React, { useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import PriceBadge from '@/components/ui/PriceBadge';
import ProductPriceChart from './components/ProductPriceChart';
import AlertSetupPanel from './components/AlertSetupPanel';
import { PRODUCTS } from '@/data/marketData';
import { Heart, Bell, ArrowLeft, Users, RefreshCw, MapPin } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

function ProductDetailContent() {
  const searchParams = useSearchParams();
  const id = searchParams?.get('id') || 'prod-tomato';
  const product = PRODUCTS?.find(p => p?.id === id) || PRODUCTS?.[0];

  const [lang, setLang] = useState<'en' | 'ta'>('en');
  const [watched, setWatched] = useState(product?.isWatched);
  const [activeVariety, setActiveVariety] = useState(product?.varieties?.[0]?.id);
  const [showAlert, setShowAlert] = useState(false);

  const selectedVariety = product?.varieties?.find(v => v?.id === activeVariety) || product?.varieties?.[0];
  const relatedProducts = PRODUCTS?.filter(p => p?.category === product?.category && p?.id !== product?.id)?.slice(0, 4);

  const priceChartData = [
    { day: 'Mon', price: product?.priceHistory?.[0] },
    { day: 'Tue', price: product?.priceHistory?.[1] },
    { day: 'Wed', price: product?.priceHistory?.[2] },
    { day: 'Thu', price: product?.priceHistory?.[3] },
    { day: 'Fri', price: product?.priceHistory?.[4] },
    { day: 'Sat', price: product?.priceHistory?.[5] },
    { day: 'Sun', price: product?.priceHistory?.[6] },
  ];

  return (
    <AppLayout activeRoute="/product-category-browse" lang={lang} onLangToggle={() => setLang(l => l === 'en' ? 'ta' : 'en')}>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 xl:px-10 2xl:px-16 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Link href="/product-category-browse" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft size={16} />
            {lang === 'en' ? 'Browse' : 'உலாவு'}
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm text-foreground font-medium">
            {lang === 'en' ? product?.name : product?.nameTa}
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          {/* Left: Image + Quick stats */}
          <div>
            <div className="relative rounded-4xl overflow-hidden aspect-[4/3] bg-muted mb-4">
              <AppImage
                src={product?.image}
                alt={product?.imageAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
                priority
              />
              {product?.isSeasonal && (
                <div className="absolute top-4 left-4">
                  <span className="text-sm font-semibold bg-accent text-accent-foreground rounded-full px-3 py-1.5">
                    🌿 {lang === 'en' ? 'Seasonal' : 'பருவகாலம்'}
                  </span>
                </div>
              )}
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={() => {
                    setWatched(w => !w);
                    toast(watched ? `${product?.name} removed from watchlist` : `${product?.name} added to watchlist`, { icon: watched ? '🗑️' : '💚' });
                  }}
                  className={`watchlist-heart w-11 h-11 flex items-center justify-center rounded-2xl glass-card ${watched ? 'text-red-alert' : 'text-muted-foreground'}`}
                  aria-label={watched ? 'Remove from watchlist' : 'Add to watchlist'}
                >
                  <Heart size={20} fill={watched ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => setShowAlert(true)}
                  className="w-11 h-11 flex items-center justify-center rounded-2xl glass-card text-primary"
                  aria-label="Set price alert"
                >
                  <Bell size={20} />
                </button>
              </div>
            </div>

            {/* Market source badge */}
            <div className="flex items-center gap-3 p-4 bg-secondary rounded-2xl">
              <MapPin size={16} className="text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Koyambedu Wholesale Market Complex
                </p>
                <p className="text-xs text-muted-foreground">
                  {lang === 'en' ? 'Chennai, Tamil Nadu · Asia\'s largest wholesale market' : 'சென்னை, தமிழ்நாடு · ஆசியாவின் மிகப்பெரிய மொத்த சந்தை'}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Details */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {lang === 'en' ? product?.name : product?.nameTa}
                </h1>
                <p className="text-lg text-muted-foreground mt-0.5">
                  {lang === 'en' ? product?.nameTa : product?.name}
                </p>
              </div>
              <PriceBadge trend={product?.trend} change={product?.priceChange} changePct={product?.priceChangePct} size="md" />
            </div>

            {/* Watchlist count */}
            <div className="flex items-center gap-1.5 mb-6">
              <Users size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground font-tabular">
                {product?.watchlistCount?.toLocaleString('en-IN')} {lang === 'en' ? 'people tracking this' : 'பேர் இதை கண்காணிக்கிறார்கள்'}
              </span>
            </div>

            {/* Price display */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: lang === 'en' ? 'Min Price' : 'குறைந்தபட்சம்', value: selectedVariety?.minPrice, color: 'text-drop-green' },
                { label: lang === 'en' ? 'Modal Price' : 'முக்கிய விலை', value: selectedVariety?.modalPrice, color: 'text-foreground', highlight: true },
                { label: lang === 'en' ? 'Max Price' : 'அதிகபட்சம்', value: selectedVariety?.maxPrice, color: 'text-rise-red' },
              ]?.map(item => (
                <div key={`price-${item?.label}`} className={`p-4 rounded-2xl text-center ${item?.highlight ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <p className={`text-xs font-semibold mb-1 ${item?.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {item?.label}
                  </p>
                  <p className={`text-2xl font-bold font-tabular ${item?.highlight ? 'text-primary-foreground' : item?.color}`}>
                    ₹{item?.value}
                  </p>
                  <p className={`text-xs ${item?.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    /{product?.unit}
                  </p>
                </div>
              ))}
            </div>

            {/* Yesterday comparison */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-2xl mb-6">
              <RefreshCw size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {lang === 'en' ? 'Yesterday:' : 'நேற்று:'}
                <span className="font-semibold text-foreground ml-1 font-tabular">₹{product?.previousPrice}/{product?.unit}</span>
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {lang === 'en' ? 'Updated' : 'புதுப்பிக்கப்பட்டது'} {product?.lastUpdated}
              </span>
            </div>

            {/* Variety selector */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-foreground mb-3">
                {lang === 'en' ? 'Select Variety' : 'வகையை தேர்வு செய்யவும்'}
              </p>
              <div className="flex flex-wrap gap-2">
                {product?.varieties?.map(variety => (
                  <button
                    key={`variety-${variety?.id}`}
                    onClick={() => setActiveVariety(variety?.id)}
                    className={`px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all duration-200 ${
                      activeVariety === variety?.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-border hover:border-primary hover:text-primary'
                    }`}
                  >
                    <span className="block">{lang === 'en' ? variety?.name : variety?.nameTa}</span>
                    <span className="block text-xs font-tabular opacity-75 mt-0.5">₹{variety?.modalPrice}/{product?.unit}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Seasonal note */}
            {product?.isSeasonal && product?.seasonNote && (
              <div className="p-4 bg-amber-light border border-amber-200 rounded-2xl mb-6">
                <p className="text-sm font-semibold text-amber-800">
                  🌿 {lang === 'en' ? product?.seasonNote : product?.seasonNoteTa}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setWatched(w => !w);
                  toast(watched ? `${product?.name} removed from watchlist` : `${product?.name} added to watchlist`, { icon: watched ? '🗑️' : '💚' });
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm border transition-all duration-200 active:scale-95 ${
                  watched
                    ? 'bg-red-light text-rise-red border-red-200 hover:bg-red-100' :'bg-secondary text-primary border-border hover:bg-green-light'
                }`}
              >
                <Heart size={16} fill={watched ? 'currentColor' : 'none'} />
                {watched
                  ? (lang === 'en' ? 'Watching' : 'கண்காணிக்கிறேன்')
                  : (lang === 'en' ? 'Add to Watchlist' : 'கண்காணிப்பில் சேர்')}
              </button>
              <button
                onClick={() => setShowAlert(true)}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm bg-primary text-primary-foreground transition-all duration-200 hover:opacity-90 active:scale-95"
              >
                <Bell size={16} />
                {lang === 'en' ? 'Set Alert' : 'எச்சரிக்கை அமை'}
              </button>
            </div>
          </div>
        </div>

        {/* Price History Chart */}
        <div className="card-base p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-foreground">
                {lang === 'en' ? '7-Day Price Trend' : '7 நாள் விலை போக்கு'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {lang === 'en' ? 'Modal price at Koyambedu market' : 'கோயம்பேடு சந்தையில் முக்கிய விலை'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary" />
              <span className="text-xs text-muted-foreground font-tabular">₹/kg</span>
            </div>
          </div>
          <ProductPriceChart data={priceChartData} trend={product?.trend} />
        </div>

        {/* Variety Comparison Table */}
        {product?.varieties?.length > 1 && (
          <div className="card-base p-6 mb-8 overflow-x-auto">
            <h2 className="text-lg font-bold text-foreground mb-4">
              {lang === 'en' ? 'Variety Comparison' : 'வகை ஒப்பீடு'}
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-semibold text-muted-foreground">
                    {lang === 'en' ? 'Variety' : 'வகை'}
                  </th>
                  <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Min</th>
                  <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Modal</th>
                  <th className="text-right py-3 pl-4 font-semibold text-muted-foreground">Max</th>
                </tr>
              </thead>
              <tbody>
                {product?.varieties?.map((variety, idx) => (
                  <tr key={`vrow-${variety?.id}`} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/40'}`}>
                    <td className="py-3 pr-4">
                      <p className="font-semibold text-foreground">{lang === 'en' ? variety?.name : variety?.nameTa}</p>
                      <p className="text-xs text-muted-foreground">{lang === 'en' ? variety?.nameTa : variety?.name}</p>
                    </td>
                    <td className="text-right py-3 px-4 font-tabular text-drop-green font-semibold">₹{variety?.minPrice}</td>
                    <td className="text-right py-3 px-4">
                      <span className="font-tabular font-bold text-foreground bg-secondary rounded-lg px-2 py-1">₹{variety?.modalPrice}</span>
                    </td>
                    <td className="text-right py-3 pl-4 font-tabular text-rise-red font-semibold">₹{variety?.maxPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-foreground mb-4">
              {lang === 'en' ? 'More in this Category' : 'இந்த வகையில் மேலும்'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {relatedProducts?.map(rp => (
                <Link key={`related-${rp?.id}`} href={`/product-detail?id=${rp?.id}`} className="block group">
                  <div className="card-base card-hover overflow-hidden">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <AppImage
                        src={rp?.image}
                        alt={rp?.imageAlt}
                        fill
                        sizes="25vw"
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-sm text-foreground truncate">
                        {lang === 'en' ? rp?.name : rp?.nameTa}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="price-value text-sm">₹{rp?.currentPrice}</span>
                        <PriceBadge trend={rp?.trend} change={rp?.priceChange} changePct={rp?.priceChangePct} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Alert Setup Modal */}
      {showAlert && (
        <AlertSetupPanel
          product={product}
          lang={lang}
          onClose={() => setShowAlert(false)}
        />
      )}
    </AppLayout>
  );
}

export default function ProductDetailPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    }>
      <ProductDetailContent />
    </Suspense>
  );
}